import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const SuperAdminUserManagement = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading, signOut } = useAuth();
  
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Check super admin access
  useEffect(() => {
    if (!authLoading && userProfile?.role !== 'super_admin') {
      navigate('/admin-login', { replace: true });
    }
  }, [authLoading, userProfile, navigate]);

  // Load admins
  useEffect(() => {
    if (!authLoading && userProfile?.role === 'super_admin') {
      loadAdmins();
    }
  }, [authLoading, userProfile]);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminService?.getAllSuperAdmins();
      setAdmins(data || []);
    } catch (err) {
      console.error('Error loading admins:', err);
      setError('Erreur lors du chargement des administrateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e?.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      // Validate form
      if (!formData?.email || !formData?.password || !formData?.fullName) {
        throw new Error('Tous les champs sont requis');
      }

      if (formData?.password?.length < 8) {
        throw new Error('Le mot de passe doit contenir au moins 8 caractères');
      }

      // Check email availability
      const isAvailable = await adminService?.checkEmailAvailability(formData?.email);
      if (!isAvailable) {
        throw new Error('Cet email est déjà utilisé');
      }

      // Create admin
      await adminService?.createSuperAdmin(formData?.email, formData?.password, formData?.fullName);
      
      // Reset form and reload
      setFormData({ email: '', password: '', fullName: '' });
      setShowCreateModal(false);
      await loadAdmins();
      
      alert('✅ Super admin créé avec succès');
    } catch (err) {
      console.error('Error creating admin:', err);
      setFormError(err?.message || 'Erreur lors de la création');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditAdmin = async (e) => {
    e?.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      if (!formData?.fullName) {
        throw new Error('Le nom complet est requis');
      }

      await adminService?.updateSuperAdmin(selectedAdmin?.id, {
        fullName: formData?.fullName
      });
      
      setFormData({ email: '', password: '', fullName: '' });
      setShowEditModal(false);
      setSelectedAdmin(null);
      await loadAdmins();
      
      alert('✅ Super admin modifié avec succès');
    } catch (err) {
      console.error('Error updating admin:', err);
      setFormError(err?.message || 'Erreur lors de la modification');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteAdmin = async () => {
    setFormLoading(true);
    setFormError('');

    try {
      // Prevent self-deletion
      if (selectedAdmin?.id === userProfile?.id) {
        throw new Error('Vous ne pouvez pas supprimer votre propre compte');
      }

      await adminService?.deleteSuperAdmin(selectedAdmin?.id);
      
      setShowDeleteModal(false);
      setSelectedAdmin(null);
      await loadAdmins();
      
      alert('✅ Super admin supprimé avec succès');
    } catch (err) {
      console.error('Error deleting admin:', err);
      setFormError(err?.message || 'Erreur lors de la suppression');
    } finally {
      setFormLoading(false);
    }
  };

  const handleResetPassword = async (admin) => {
    const newPassword = prompt('Entrez le nouveau mot de passe (min. 8 caractères):');
    
    if (!newPassword) return;
    
    if (newPassword?.length < 8) {
      alert('❌ Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    try {
      await adminService?.resetAdminPassword(admin?.id, newPassword);
      alert('✅ Mot de passe réinitialisé avec succès');
    } catch (err) {
      console.error('Error resetting password:', err);
      alert(`❌ Erreur: ${err?.message}`);
    }
  };

  const openCreateModal = () => {
    setFormData({ email: '', password: '', fullName: '' });
    setFormError('');
    setShowCreateModal(true);
  };

  const openEditModal = (admin) => {
    setSelectedAdmin(admin);
    setFormData({ email: admin?.email, password: '', fullName: admin?.fullName });
    setFormError('');
    setShowEditModal(true);
  };

  const openDeleteModal = (admin) => {
    setSelectedAdmin(admin);
    setFormError('');
    setShowDeleteModal(true);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const filteredAdmins = admins?.filter(admin => 
    admin?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    admin?.fullName?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: fr });
    } catch {
      return '-';
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0F0F12] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
          <p className="text-secondary">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Gestion des Super Admins - EndoReferral</title>
      </Helmet>
      <div className="min-h-screen bg-[#0F0F12]">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-xl border-b border-white/8 shadow-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/super-admin-dashboard')}
                  className="p-2 text-muted hover:text-primary hover:bg-card-hover rounded-lg transition-colors"
                >
                  <Icon name="ArrowLeft" size={20} />
                </button>
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Icon name="Shield" size={24} className="text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-primary">Gestion des Super Admins</h1>
                  <p className="text-sm text-muted">{filteredAdmins?.length} administrateur(s)</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3 px-4 py-2 bg-card-elevated rounded-xl">
                  <div className="w-8 h-8 bg-gradient-to-br from-accent to-primary rounded-lg flex items-center justify-center">
                    <Icon name="User" size={16} className="text-white" />
                  </div>
                  <div className="hidden sm:block">
                    <p className="text-sm font-medium text-primary">{userProfile?.fullName}</p>
                    <p className="text-xs text-muted">{userProfile?.email}</p>
                  </div>
                </div>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 px-4 py-2 text-error hover:bg-error-bg rounded-xl transition-colors"
                >
                  <Icon name="LogOut" size={20} />
                  <span className="hidden sm:inline">Déconnexion</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-error-bg border border-error/30 rounded-xl p-4 flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-error" />
              <p className="text-error">{error}</p>
            </div>
          )}

          {/* Actions Bar */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Icon name="Search" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                type="text"
                placeholder="Rechercher par email ou nom..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-3 bg-card border border-white/8 rounded-xl text-primary placeholder-muted focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
            <Button
              onClick={openCreateModal}
              className="flex items-center gap-2 px-6 py-3 bg-accent hover:bg-accent/90 text-white rounded-xl font-medium transition-colors"
            >
              <Icon name="UserPlus" size={20} />
              Créer un Super Admin
            </Button>
          </div>

          {/* Admins Table */}
          <div className="bg-card rounded-2xl shadow-2xl border border-white/8 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-card-elevated border-b border-white/8">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-muted uppercase tracking-wider">Administrateur</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-muted uppercase tracking-wider">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-muted uppercase tracking-wider">Créé le</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-muted uppercase tracking-wider">Dernière connexion</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-muted uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/8">
                  {filteredAdmins?.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center">
                        <Icon name="Users" size={48} className="text-muted mx-auto mb-4" />
                        <p className="text-muted">Aucun administrateur trouvé</p>
                      </td>
                    </tr>
                  ) : (
                    filteredAdmins?.map((admin) => (
                      <tr key={admin?.id} className="hover:bg-card-hover transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-accent to-primary rounded-lg flex items-center justify-center">
                              <Icon name="User" size={20} className="text-white" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-primary">{admin?.fullName}</p>
                              <p className="text-xs text-muted">Super Admin</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-secondary">{admin?.email}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-secondary">{formatDate(admin?.createdAt)}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-secondary">{formatDate(admin?.lastSignInAt)}</p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openEditModal(admin)}
                              className="p-2 text-accent hover:bg-accent/10 rounded-lg transition-colors"
                              title="Modifier"
                            >
                              <Icon name="Edit" size={18} />
                            </button>
                            <button
                              onClick={() => handleResetPassword(admin)}
                              className="p-2 text-blue-400 hover:bg-blue-400/10 rounded-lg transition-colors"
                              title="Réinitialiser le mot de passe"
                            >
                              <Icon name="Key" size={18} />
                            </button>
                            <button
                              onClick={() => openDeleteModal(admin)}
                              className="p-2 text-error hover:bg-error-bg rounded-lg transition-colors"
                              title="Supprimer"
                              disabled={admin?.id === userProfile?.id}
                            >
                              <Icon name="Trash2" size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-card rounded-2xl shadow-2xl border border-white/8 w-full max-w-md">
              <div className="p-6 border-b border-white/8">
                <h2 className="text-xl font-bold text-primary">Créer un Super Admin</h2>
              </div>
              <form onSubmit={handleCreateAdmin} className="p-6 space-y-4">
                {formError && (
                  <div className="bg-error-bg border border-error/30 rounded-xl p-3 flex items-center gap-2">
                    <Icon name="AlertCircle" size={18} className="text-error" />
                    <p className="text-sm text-error">{formError}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">Nom complet</label>
                  <Input
                    type="text"
                    value={formData?.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e?.target?.value })}
                    placeholder="Jean Dupont"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">Email</label>
                  <Input
                    type="email"
                    value={formData?.email}
                    onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                    placeholder="admin@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">Mot de passe</label>
                  <Input
                    type="password"
                    value={formData?.password}
                    onChange={(e) => setFormData({ ...formData, password: e?.target?.value })}
                    placeholder="Min. 8 caractères"
                    required
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    variant="outline"
                    className="flex-1"
                    disabled={formLoading}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-accent hover:bg-accent/90 text-white"
                    disabled={formLoading}
                  >
                    {formLoading ? 'Création...' : 'Créer'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-card rounded-2xl shadow-2xl border border-white/8 w-full max-w-md">
              <div className="p-6 border-b border-white/8">
                <h2 className="text-xl font-bold text-primary">Modifier le Super Admin</h2>
              </div>
              <form onSubmit={handleEditAdmin} className="p-6 space-y-4">
                {formError && (
                  <div className="bg-error-bg border border-error/30 rounded-xl p-3 flex items-center gap-2">
                    <Icon name="AlertCircle" size={18} className="text-error" />
                    <p className="text-sm text-error">{formError}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">Email (non modifiable)</label>
                  <Input
                    type="email"
                    value={formData?.email}
                    disabled
                    className="bg-card-elevated cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">Nom complet</label>
                  <Input
                    type="text"
                    value={formData?.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e?.target?.value })}
                    placeholder="Jean Dupont"
                    required
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    variant="outline"
                    className="flex-1"
                    disabled={formLoading}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-accent hover:bg-accent/90 text-white"
                    disabled={formLoading}
                  >
                    {formLoading ? 'Modification...' : 'Modifier'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-card rounded-2xl shadow-2xl border border-white/8 w-full max-w-md">
              <div className="p-6 border-b border-white/8">
                <h2 className="text-xl font-bold text-error">Supprimer le Super Admin</h2>
              </div>
              <div className="p-6 space-y-4">
                {formError && (
                  <div className="bg-error-bg border border-error/30 rounded-xl p-3 flex items-center gap-2">
                    <Icon name="AlertCircle" size={18} className="text-error" />
                    <p className="text-sm text-error">{formError}</p>
                  </div>
                )}
                <div className="bg-error-bg border border-error/30 rounded-xl p-4">
                  <p className="text-sm text-error mb-2">⚠️ Attention : Cette action est irréversible</p>
                  <p className="text-sm text-secondary">Vous êtes sur le point de supprimer :</p>
                  <p className="text-sm font-medium text-primary mt-2">{selectedAdmin?.fullName}</p>
                  <p className="text-sm text-muted">{selectedAdmin?.email}</p>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    onClick={() => setShowDeleteModal(false)}
                    variant="outline"
                    className="flex-1"
                    disabled={formLoading}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="button"
                    onClick={handleDeleteAdmin}
                    className="flex-1 bg-error hover:bg-error/90 text-white"
                    disabled={formLoading}
                  >
                    {formLoading ? 'Suppression...' : 'Supprimer'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SuperAdminUserManagement;